"use client";

import { Button } from "@thilke/ui/components/button";
import { cn } from "@thilke/ui/lib/utils";
import { FileJson } from "lucide-react";
import * as React from "react";

export function ComponentWrapper({
  className,
  name,
  origin,
  children,
  allowOverflow = false,
  previewClassName,
  ...props
}: React.ComponentPropsWithoutRef<"div"> & {
  name: string;
  origin: string;
  allowOverflow?: boolean;
  previewClassName?: string;
}) {
  const displayName = getComponentName(name);
  const registryLabel = name;

  return (
    <ComponentErrorBoundary name={name}>
      <div
        id={name}
        data-name={name.toLowerCase()}
        className={cn(
          "flex w-full min-w-0 scroll-mt-16 flex-col rounded-lg border bg-card",
          allowOverflow ? "overflow-visible" : "overflow-hidden",
          className,
        )}
        {...props}
      >
        <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
          <div>
            <div className="text-sm font-medium">{displayName}</div>
            <div className="font-mono text-xs text-muted-foreground">
              {registryLabel}
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <a
              href={`${origin}/r/${name}.json`}
              target="_blank"
              rel="noreferrer"
            >
              <FileJson className="size-4" /> JSON
            </a>
          </Button>
        </div>
        <div
          className={cn(
            "flex min-w-0 flex-1 items-center justify-center p-6",
            allowOverflow ? "overflow-visible" : "overflow-x-auto",
            previewClassName,
          )}
        >
          {children}
        </div>
      </div>
    </ComponentErrorBoundary>
  );
}

class ComponentErrorBoundary extends React.Component<
  { children: React.ReactNode; name: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; name: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Error in component ${this.props.name}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-destructive">
          Something went wrong in component: {this.props.name}
        </div>
      );
    }

    return this.props.children;
  }
}

function getComponentName(name: string) {
  return name.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}
